import React, { useCallback, useMemo, useState } from 'react';
import AccountsSubtable from './AccountsSubtable';
import ApplySuggestionButton from './ApplySuggestionButton';
import FeeSnapshotsDisclosure from './FeeSnapshots';
import ReserveLineageDisclosure from './ReserveLineage';
import TransactionsPanel from './TransactionsPanel';
import cashReservesService from '../../services/cashReservesService';
import { MarkAllReviewedButton } from './ReviewActions';
import { fmtCurrencyPrecise, isManagementFee, isRecurringFlagAlert, mergeAlerts, mergeReserves } from './helpers';

// Shown in full: accounts in a family share a surname, so the part that tells
// them apart ("- Roth IRA", "- Schwab One TOD") is at the END of the name.
// Bootstrap's .badge would otherwise shrink the name to .75em and hold it on
// one line; 1em restores the size of the text it sits beside.
const AccountBadge = ({ accountName, accountNumber }) => (
  <span
    className="badge bg-light text-secondary border fw-normal"
    style={{ fontSize: '1em', whiteSpace: 'normal', textAlign: 'left' }}
    title={`${accountName || 'Unknown'} · ${accountNumber}`}
  >
    {accountName || accountNumber}
  </span>
);

// Hide this suggestion TYPE for this account (backend-enforced, shared by all
// users). The confirm matters: one click affects everyone's view of the
// account; undo lives in the dashboard's "Hidden suggestions" dropdown.
const HideSuggestionButton = ({ alert, accountNumber, accountName, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const who = accountName || accountNumber;
  const handleClick = async () => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Hide "${alert.label}" suggestions for ${who}? Re-enable them from the Hidden suggestions menu.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await cashReservesService.addAlertIgnore(alert.action, accountNumber);
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Could not hide the suggestion type:', err);
    } finally {
      setBusy(false);
    }
  };
  // Styled as a peer of the Apply button beside it — a bare icon read as
  // decoration, and hiding a suggestion is a real action worth a label.
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary py-0"
      disabled={busy}
      onClick={handleClick}
      title={`Hide "${alert.label}" suggestions for ${who} (re-enable from Hidden suggestions)`}
    >
      <i className={`bi ${busy ? 'bi-hourglass-split' : 'bi-eye-slash'} me-1`}></i>
      {busy ? 'Ignoring…' : 'Ignore'}
    </button>
  );
};

/**
 * Everything about one account family, in a single expansion.
 *
 * The same component renders the ungrouped view by receiving a one-account
 * "family", so the two views cannot drift apart. With one account the Account
 * columns are suppressed — they'd repeat the same value down every row.
 */
const ConsolidatedPanel = ({
  accounts,
  days,
  missingManagementFee,
  matchCount,
  matchesFilters,
  sortConfig,
  onSort,
  onReviewed,
  onIgnored,
  onAnnotationChanged,
  onApplied,
  onCombined,
  onAlertIgnoresChanged,
  // The dashboard's "Filter Actions" selection. Empty = no filter. The family
  // list already narrows by it; this narrows the panel's Suggested Actions
  // too, so a filtered view never shows non-selected action types.
  selectedSuggestedActions = [],
  ignoreManagementFee = false,
  reloadToken = 0,
}) => {
  // Which accounts the sections below are narrowed to. An EMPTY set means "no
  // filter" rather than "nothing": seeding it from props would make initial
  // state depend on props (and need a sync effect on every reload), and it
  // makes an empty panel unreachable.
  const [selected, setSelected] = useState(() => new Set());

  // Saving a transaction link changes which quarters read as Linked, so the
  // fee-snapshot section has to re-read that account's snapshots — otherwise
  // the badge stays stale until the section is reopened. Carries the account
  // number so only the one that changed is refetched, not the whole family.
  const [snapshotReload, setSnapshotReload] = useState({ accountNumber: null, token: 0 });
  const notifySnapshots = useCallback((accountNumber) => {
    setSnapshotReload((prev) => ({ accountNumber, token: prev.token + 1 }));
  }, []);

  // Fed by TransactionsPanel's onRowsChanged so its cash-flow summary can be
  // shown inline in the title here instead of its own strip below.
  const [txSummary, setTxSummary] = useState(null);
  const handleTxRowsChanged = useCallback(({ summary }) => setTxSummary(summary), []);

  // Both paths that write a transaction link route through here on their way to
  // the parent. Combining reserves deliberately does NOT: it writes to
  // reserve_id_links, while a snapshot's badge comes from an exact
  // transaction_reserve_links.reserve_id match, so a combine can't change it.
  const handleAnnotationChanged = useCallback(
    (accountNumber) => {
      notifySnapshots(accountNumber);
      if (onAnnotationChanged) onAnnotationChanged(accountNumber);
    },
    [notifySnapshots, onAnnotationChanged]
  );

  const handleApplied = useCallback(
    (accountNumber) => {
      notifySnapshots(accountNumber);
      if (onApplied) onApplied(accountNumber);
    },
    [notifySnapshots, onApplied]
  );

  const visibleAccounts = useMemo(() => {
    if (selected.size === 0) return accounts;
    const kept = accounts.filter((a) => selected.has(a.account_number));
    // The selection can go stale — toggling "Ignore Management Fee-only
    // accounts" drops accounts from the family while this panel stays mounted.
    // Falling back to the whole family beats a blank panel the user can't
    // explain. Stale numbers stay in the set and reactivate if they return.
    return kept.length > 0 ? kept : accounts;
  }, [accounts, selected]);

  // Derived from the result, not from selected.size, so every downstream
  // display stays truthful in the stale case.
  const filterActive = visibleAccounts.length < accounts.length;
  const selectable = accounts.length > 1;

  const toggleAccount = useCallback((accountNumber) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(accountNumber)) next.delete(accountNumber);
      else next.add(accountNumber);
      return next;
    });
  }, []);

  // Empty and full sets both render everything, so this marks or clears rather
  // than showing or hiding. Its value is subtractive selection: select all,
  // then untick the one or two you don't care about.
  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      accounts.every((a) => prev.has(a.account_number))
        ? new Set()
        : new Set(accounts.map((a) => a.account_number))
    );
  }, [accounts]);

  const showAccount = visibleAccounts.length > 1;
  const selectedActionSet = useMemo(
    () => new Set(selectedSuggestedActions || []),
    [selectedSuggestedActions]
  );
  const alerts = useMemo(
    () =>
      mergeAlerts(visibleAccounts).filter(
        ({ alert }) =>
          !isRecurringFlagAlert(alert) &&
          (selectedActionSet.size === 0 || selectedActionSet.has(alert.action))
      ),
    [visibleAccounts, selectedActionSet]
  );
  const reserves = useMemo(() => mergeReserves(visibleAccounts), [visibleAccounts]);

  // Summed from the rows the table actually renders, not from the accounts —
  // a parallel per-account calculation could silently disagree with what's on
  // screen, and this way the totals follow the account selection for free.
  const reserveTotals = useMemo(() => {
    let fee = 0;
    let other = 0;
    reserves.forEach(({ reserve }) => {
      const amount = reserve.calculated_reserve_dollar || 0;
      if (isManagementFee(reserve)) fee += amount;
      else other += amount;
    });
    return { fee, other, total: fee + other };
  }, [reserves]);

  return (
    <div className="p-3">
      {/* Only rendered when there's something to show — otherwise this was an
          empty bordered bar taking up space above the Accounts table. */}
      {(missingManagementFee || (matchCount != null && matchCount < accounts.length)) && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 pb-2 border-bottom">
          {missingManagementFee && (
            <span className="badge bg-warning text-dark" title="At least one account in this family has no Management Fee reserve">
              <i className="bi bi-exclamation-triangle-fill me-1"></i>
              No mgmt fee
            </span>
          )}
          {matchCount != null && matchCount < accounts.length && (
            <span className="text-muted small">
              {matchCount} of {accounts.length} accounts match your filters — the rest are dimmed.
            </span>
          )}
        </div>
      )}

      <AccountsSubtable
        accounts={accounts}
        days={days}
        sortConfig={sortConfig}
        onSort={onSort}
        onReviewed={onReviewed}
        onIgnored={onIgnored}
        matchesFilters={matchesFilters}
        selectable={selectable}
        selected={selected}
        onToggleAccount={toggleAccount}
        onToggleAll={toggleAll}
        headerActions={<MarkAllReviewedButton accounts={visibleAccounts} days={days} onReviewed={onReviewed} />}
      />

      {/* Sits on the seam the filter acts on — roster above, filtered sections
          below — so its scope is self-evident. Deliberately worded away from
          the header bar's "match your filters" note, which is a different
          thing (the dashboard's own filters, shown as dimming). */}
      {selected.size > 0 && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="text-muted small">
            <i className="bi bi-funnel-fill me-1"></i>
            {filterActive
              ? `Showing ${visibleAccounts.length} of ${accounts.length} accounts in the sections below.`
              : `All ${accounts.length} accounts selected.`}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-link text-muted p-0"
            onClick={() => setSelected(new Set())}
          >
            <i className="bi bi-x-circle me-1"></i>Clear
          </button>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mb-3">
          <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>
            Suggested Actions
          </div>
          <ul className="list-unstyled mb-0 small">
            {alerts.map(({ alert, accountNumber, accountName, key }) => (
              <li key={key} className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span
                  className={`badge ${alert.level === 'red' ? 'bg-danger' : 'bg-warning text-dark'}`}
                  style={{ minWidth: '64px' }}
                >
                  {alert.level === 'red' ? 'RED' : 'ORANGE'}
                </span>
                {showAccount && (
                  <AccountBadge accountName={accountName} accountNumber={accountNumber} />
                )}
                <span className="fw-semibold">{alert.label}</span>
                <span className="text-muted">— {alert.detail}</span>
                {(alert.suggested_applies?.length > 0 ||
                  (alert.transaction_id != null && alert.suggested_links?.length > 0)) && (
                  <ApplySuggestionButton
                    accountNumber={accountNumber}
                    alert={alert}
                    onApplied={handleApplied}
                  />
                )}
                <HideSuggestionButton
                  alert={alert}
                  accountNumber={accountNumber}
                  accountName={accountName}
                  onChanged={onAlertIgnoresChanged}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {reserves.length > 0 && (
        <div className="mb-3">
          <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>
            Configured Reserves{' '}
            {/* The fee is a standing advisory charge worth a median 84% of a
                family's reserve target, often all of it, so the split is what
                makes the total legible; with no fee it would just restate it. */}
            <span
              role="group"
              aria-label="Reserve totals"
              style={{ letterSpacing: 'normal', textTransform: 'none' }}
              title={ignoreManagementFee ? 'Management fee is not counted in the Reserve Target column' : undefined}
            >
              (
              {reserveTotals.fee > 0 && (
                <>
                  Management fee <span className="fw-semibold">{fmtCurrencyPrecise(reserveTotals.fee)}</span>
                  {' · '}
                  Other <span className="fw-semibold">{fmtCurrencyPrecise(reserveTotals.other)}</span>
                  {' · '}
                </>
              )}
              Total <span className="fw-bold">{fmtCurrencyPrecise(reserveTotals.total)}</span>
              )
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0 bg-white">
              <thead>
                <tr>
                  {/* Floor the column so short names aren't squeezed narrow;
                      it still grows for longer ones and wraps when it can't. */}
                  {showAccount && <th style={{ minWidth: '12rem' }}>Account</th>}
                  <th>Category</th>
                  <th className="text-end">Reserve</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Target Remaining</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {reserves.map(({ reserve: r, accountNumber, accountName, key }) => (
                  <tr key={key} className={r.potential_duplicate ? 'table-warning' : undefined}>
                    {showAccount && (
                      <td title={`${accountName} · ${accountNumber}`}>
                        {accountName || accountNumber}
                      </td>
                    )}
                    <td>
                      {r.reserve_category || 'Uncategorized'}
                      {r.potential_duplicate && (
                        <i
                          className="bi bi-exclamation-diamond-fill text-warning ms-2"
                          title="Potential duplicate — matches another reserve's value, start date, and note"
                        ></i>
                      )}
                    </td>
                    <td className="text-end">{fmtCurrencyPrecise(r.calculated_reserve_dollar)}</td>
                    <td>{r.start_date || <span className="text-muted">—</span>}</td>
                    <td>{r.end_date || <span className="text-muted">—</span>}</td>
                    <td>
                      {r.computed_target != null ? (
                        <span title={`$${(r.cumulative_withdrawn || 0).toLocaleString()} withdrawn to date`}>
                          {fmtCurrencyPrecise(r.computed_target)} of {fmtCurrencyPrecise(r.starting_value)}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{r.note || <span className="text-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mb-3">
        <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>
          Transactions · last {days} days{' '}
          {txSummary && (
            <span
              role="group"
              aria-label="Transaction totals"
              style={{ letterSpacing: 'normal', textTransform: 'none' }}
            >
              (Transactions <span className="fw-semibold">{txSummary.transaction_count}</span>
              {' · '}
              Inflow <span className="fw-semibold text-success">{fmtCurrencyPrecise(txSummary.inflow)}</span>
              {' · '}
              Outflow <span className="fw-semibold text-danger">{fmtCurrencyPrecise(txSummary.outflow)}</span>
              {' · '}
              Net{' '}
              <span
                className={`fw-semibold ${(txSummary.net_cash_impact ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}
              >
                {fmtCurrencyPrecise(txSummary.net_cash_impact)}
              </span>
              )
            </span>
          )}
        </div>
        {/* accounts = fetch scope, visibleAccounts = render scope: narrowing
            the selection must not re-run either component's fetch effect. */}
        <TransactionsPanel
          accounts={accounts}
          visibleAccounts={visibleAccounts}
          days={days}
          reloadToken={reloadToken}
          onAnnotationChanged={handleAnnotationChanged}
          onRowsChanged={handleTxRowsChanged}
          hideSummaryStrip
        />
      </div>

      <FeeSnapshotsDisclosure
        accounts={accounts}
        visibleAccounts={visibleAccounts}
        reload={snapshotReload}
      />

      <ReserveLineageDisclosure
        accounts={accounts}
        visibleAccounts={visibleAccounts}
        onCombined={onCombined}
      />
    </div>
  );
};

export default ConsolidatedPanel;
